// syncManager.ts ------------------------------------------------------
import { createId } from '@paralleldrive/cuid2'
import Dexie from 'dexie'
import type { Table, Transaction, UpdateSpec } from 'dexie'
import { EventEmitter } from 'eventemitter3'
import { isEqual, minBy } from 'lodash-es'

import type { RowBase } from './helpers'
import { hardDelete, SoftDeleteAddon } from './soft-delete-addon'
import { endsSyncRound, syncFailureReason } from './sync-errors'
import type { PullResponse, PushResponse, SyncableCfg } from './types'

// Extend Dexie Transaction type
declare module 'dexie' {
  interface Transaction {
    ctx?: {
      /** A row as the server sent it: nothing to push */
      fromServer?: boolean
    }
  }
}

/** The smallest page a pull asks for after timeouts cut it down (K42) */
export const MIN_PAGE_SIZE = 100

// Add new type definitions
export type SyncStatus = 'idle' | 'syncing'

export interface StatusChangeEvent {
  type: 'global' | 'syncable'
  status: SyncStatus
  previousStatus: SyncStatus
  syncableName?: string
}

export interface GetStatusOptions {
  syncableName: string
}

/** Syncs failing since `since`, the latest with `error`. */
export interface SyncFailure {
  since: Date
  error: unknown
}

type SyncEvents = {
  statusChange: [StatusChangeEvent]
  syncableStatusChange: [StatusChangeEvent]
  failureChange: [SyncFailure | null]
}

export class SyncManager extends EventEmitter<SyncEvents> {
  private readonly syncableStatuses = new Map<string, SyncStatus>()

  constructor(private readonly syncables: Array<SyncableCfg<any, any>>) {
    super()
    // Initialize all syncables as idle
    for (const syncable of this.syncables) {
      this.syncableStatuses.set(syncable.name, 'idle')
    }
    this.installHooks()
  }

  /* ───────────────── public ───────────────── */

  /** Get global sync status or specific syncable status */
  getStatus(): SyncStatus
  getStatus(options: GetStatusOptions): SyncStatus
  getStatus(options?: GetStatusOptions): SyncStatus {
    if (options?.syncableName) {
      const status = this.syncableStatuses.get(options.syncableName)
      if (status === undefined) {
        throw new Error(`Syncable '${options.syncableName}' not found`)
      }
      return status
    }
    return this.getGlobalStatus()
  }

  /**
   * Sync exactly one collection. One sync per collection at a time: a call while
   * one is running gets that run's promise and makes it run once more at the end,
   * so overlapping triggers (realtime events, polling) don't sync side by side
   * (K36).
   */
  sync(name: string): Promise<void> {
    const running = this.running.get(name)
    if (running) {
      this.syncAgain.add(name)
      return running
    }

    const run = (async () => {
      try {
        do {
          this.syncAgain.delete(name)
          await this.syncOnce(name)
        } while (this.syncAgain.has(name))
        this.clearFailure(name)
      } catch (error) {
        this.setFailure(name, error)
        throw error
      } finally {
        this.syncAgain.delete(name)
        this.running.delete(name)
      }
    })()
    this.running.set(name, run)
    return run
  }

  /**
   * Whether syncing is failing: since the first failure of a collection that
   * hasn't synced since, with the latest error. `null` when every collection's
   * last sync worked.
   */
  getFailure(): SyncFailure | null {
    const failures = [...this.failures.values()]
    if (!failures.length) return null
    // Entries are kept in the order they last failed: the last one is the latest
    const since = minBy(failures, (failure) => failure.since.getTime())!.since
    return { since, error: failures.at(-1)!.error }
  }

  /** Delay before pulling again when the server held rows back */
  pendingRetryMs = 1000

  /**
   * Sync all collections in sequence. A failure of one collection's own (the
   * server refusing its push, a photo it can't upload) doesn't hold back the
   * ones after it, the photos behind the interventi; a failure every request
   * would meet (no connection, no session) ends the round. The first error is
   * thrown at the end.
   */
  async syncAll(): Promise<void> {
    const errors: unknown[] = []
    for (const syncable of this.syncables) {
      try {
        await this.sync(syncable.name)
      } catch (error) {
        errors.push(error)
        if (endsSyncRound(syncFailureReason(error))) break
      }
    }
    if (errors.length) throw errors[0]
  }

  /** Check if any syncable has pending local changes that need to be pushed */
  async hasPendingChanges(): Promise<boolean> {
    for (const syncable of this.syncables) {
      const cfg = this.getSyncable<RowBase, unknown>(syncable.name)
      const pending = await this.getPendingChangesCount(cfg)
      if (pending > 0) {
        return true
      }
    }

    return false
  }

  /* ──────────────── private helpers ──────────────── */

  private readonly running = new Map<string, Promise<void>>()
  private readonly syncAgain = new Set<string>()
  // Per collection failing since its last sync that worked, latest last
  private readonly failures = new Map<string, SyncFailure>()
  // Page sizes cut down after a pull timed out, for as long as the app runs
  private readonly pageSizes = new Map<string, number>()
  private readonly retryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >()

  private setFailure(name: string, error: unknown): void {
    const since = this.failures.get(name)?.since ?? new Date()
    this.failures.delete(name) // re-inserted last: the latest failure
    this.failures.set(name, { since, error })
    this.emit('failureChange', this.getFailure())
  }

  private clearFailure(name: string): void {
    if (!this.failures.delete(name)) return
    this.emit('failureChange', this.getFailure())
  }

  private async syncOnce(name: string): Promise<void> {
    const cfg = this.getSyncable(name)

    // Set status to syncing
    this.setSyncableStatus(name, 'syncing')

    let pending = false
    try {
      // Push first: a row the pull finds still unsent waits for its push answer
      await this.pushDirty(cfg)
      pending = await this.pullRemote(cfg)
    } finally {
      // Always reset status to idle, even if an error occurred
      this.setSyncableStatus(name, 'idle')
    }

    if (pending) this.retryLater(name)
  }

  /**
   * Pulls again shortly. With realtime up, the next pull would otherwise wait for
   * the next change to the collection, possibly hours on a quiet day.
   */
  private retryLater(name: string): void {
    if (this.retryTimers.has(name)) return
    const timer = setTimeout(() => {
      this.retryTimers.delete(name)
      this.sync(name).catch((error) => {
        console.warn(`Pull retry failed for '${name}'`, error)
      })
    }, this.pendingRetryMs)
    this.retryTimers.set(name, timer)
  }

  private getGlobalStatus(): SyncStatus {
    for (const status of this.syncableStatuses.values()) {
      if (status === 'syncing') {
        return 'syncing'
      }
    }
    return 'idle'
  }

  private setSyncableStatus(syncableName: string, status: SyncStatus): void {
    const previousStatus = this.syncableStatuses.get(syncableName)
    if (previousStatus === undefined) {
      throw new Error(`Syncable '${syncableName}' not found`)
    }

    if (previousStatus === status) {
      return // No change, don't emit events
    }

    const previousGlobalStatus = this.getGlobalStatus()
    this.syncableStatuses.set(syncableName, status)
    const newGlobalStatus = this.getGlobalStatus()

    // Emit syncable-specific status change
    this.emit('syncableStatusChange', {
      type: 'syncable',
      status,
      previousStatus,
      syncableName: syncableName,
    } as StatusChangeEvent)

    // Emit global status change if it changed
    if (previousGlobalStatus !== newGlobalStatus) {
      this.emit('statusChange', {
        type: 'global',
        status: newGlobalStatus,
        previousStatus: previousGlobalStatus,
      } as StatusChangeEvent)
    }
  }

  private getSyncable<R extends RowBase, CP>(name: string): Normalized<R, CP> {
    const raw = this.syncables.find((syncable) => syncable.name === name)
    if (!raw) throw new Error(`No syncable for '${name}'`)
    // This cast is necessary because we're using generic type parameters
    // that we can't fully type check at runtime
    const base = raw as unknown as SyncableCfg<R, CP>

    return {
      updatedField: 'updated_at',
      deletedField: 'deleted_at',
      ...base,
    }
  }

  /** Install soft delete addon and timestamp hooks */
  private installHooks(): void {
    // Group syncables by database to apply soft delete addon once per DB
    const dbMap = new Map<
      Dexie,
      Array<{ name: string; updatedField: string; deletedField: string }>
    >()

    for (const base of this.syncables) {
      const {
        name,
        db,
        updatedField = 'updated_at',
        deletedField = 'deleted_at',
      } = base

      if (!dbMap.has(db)) {
        dbMap.set(db, [])
      }
      dbMap.get(db)!.push({ name, updatedField, deletedField })
    }

    // Apply soft delete addon to each database
    for (const [db, tables] of dbMap) {
      // Extract table names for the addon
      const enabledTables = tables.map((t) => t.name)

      // Apply soft delete addon (assumes all tables in this DB use same field names)
      const firstTable = tables[0]
      SoftDeleteAddon(db, {
        updatedField: firstTable.updatedField,
        deletedField: firstTable.deletedField,
        enabledTables,
      })
    }

    // A local write to a collection that pushes marks its row unsent, with a
    // new local version and its write time (docs/sync/UNSENT.md)
    for (const base of this.syncables) {
      if (!base.push) continue
      const { name, db, updatedField = 'updated_at' } = base
      const table = db.table(name) as Table<RowBase, string>

      table.hook('creating', (_primKey, obj, trans) => {
        if (writtenAsServer(trans)) return
        obj[updatedField] = new Date()
        obj._dirty = 1
        obj._rev = createId()
      })

      // Never stamped before the version it changes: on a row whose updated_at
      // is ahead of this clock (written by a phone running ahead, or before this
      // clock was set back) the server would refuse an edit stamped now, though
      // it was made on the latest version (K17, K47)
      table.hook('updating', (mods, _key, stored, trans) => {
        if (writtenAsServer(trans)) return
        // A put of an unchanged row (the form saves every photo it shows) is
        // no change, though Dexie fires for it (dates and files re-read from
        // the database are new objects)
        if (!changesAnything(mods, stored, updatedField))
          return {
            [updatedField]: stored[updatedField],
            _dirty: stored._dirty,
            _rev: stored._rev,
          }
        const now = Date.now()
        const before = timeOf(stored[updatedField])
        return {
          [updatedField]: new Date(before >= now ? before + 1 : now),
          _dirty: 1,
          _rev: createId(),
        }
      })
    }
  }

  /* ---------- PUSH ---------- */

  private async pushDirty<R extends RowBase, CP>(
    cfg: Normalized<R, CP>
  ): Promise<void> {
    const { name, db, push, updatedField, deletedField } = cfg
    if (!push) {
      console.info(`Push handler not specified`)
      return
    }

    const table = db.table(name) as Table<R, string>
    const primaryKey = primaryKeyOf(table)

    const meta = await this.meta<CP>(db, name)
    // Not read any more: what app versions before the marker read to find
    // unsent rows. Set once and left, so one running on this device again (a
    // tab left open, a rollback) pushes too much rather than too little
    if (meta.lastPush == null) {
      await this.setMeta<CP>(db, name, { lastPush: new Date(Date.now() - 1) })
    }

    const unsent = await table.where('_dirty').equals(1).sortBy(updatedField)
    // By key: a holdBack may answer with copies of the rows it was given
    const held = new Set(
      (push.holdBack ? await push.holdBack(unsent) : []).map(
        (row) => row[primaryKey]
      )
    )
    const dirty = unsent.filter((row) => !held.has(row[primaryKey]))
    if (!dirty.length) return

    // Each row's local version as read: one that changes while the push is in
    // flight was edited (or written by the server) meanwhile (K1)
    const sentRevs = new Map(dirty.map((row) => [row[primaryKey], row._rev]))

    const batch = push.batchSize ?? dirty.length
    let lastPushCheckpoint = meta.lastPushCheckpoint
    let failed: { error: unknown } | undefined

    for (let i = 0; i < dirty.length; i += batch) {
      const batchRows = dirty.slice(i, i + batch)
      const result: PushResponse<R, CP> | void = await push.handler(
        batchRows,
        lastPushCheckpoint
      )

      if (result && result.checkpoint !== undefined) {
        lastPushCheckpoint = result.checkpoint
      }

      await this.recordPushed(
        table,
        primaryKey,
        deletedField,
        result?.sent ?? batchRows,
        result?.records ?? [],
        sentRevs
      )

      if (result && 'error' in result) {
        // Some rows of the batch didn't go. For a reason of their own (a photo
        // S3 refuses) the batches after it still go; for one every request
        // would meet (offline, a timeout) they would fail the same way
        if (endsSyncRound(syncFailureReason(result.error))) throw result.error
        failed ??= { error: result.error }
      }
    }

    if (lastPushCheckpoint !== undefined) {
      await this.setMeta<CP>(db, name, { lastPushCheckpoint })
    }
    if (failed) throw failed.error
  }

  /**
   * Records a batch's outcome on the device, in one server transaction. A row
   * that reached the server is no longer unsent, answered or not (pushChanges
   * answers only what it wrote; waiting for an answer would push it forever),
   * unless it changed while in flight: then it goes out with the next push, or
   * a newer server copy already landed. Answers for rows not sent are applied,
   * unless the row is unsent here.
   */
  private recordPushed<R extends RowBase>(
    table: Table<R, string>,
    primaryKey: string,
    deletedField: string,
    sent: R[],
    records: R[],
    sentRevs: Map<unknown, string | undefined>
  ): Promise<void> {
    const answers = new Map<string, R>()
    for (const record of records) {
      const key = record[primaryKey]
      if (key == null) {
        console.warn(
          `[SyncManager] Missing primary key '${primaryKey}' in push response for '${table.name}'`
        )
        continue
      }
      answers.set(key as string, record)
    }

    return table.db.transaction('rw', table, async (transaction) => {
      transaction.ctx = { fromServer: true }

      const sentKeys = [
        ...new Set(sent.map((row) => row[primaryKey] as string)),
      ]
      const sentLocals = await table.bulkGet(sentKeys)
      for (const [n, key] of sentKeys.entries()) {
        const answer = answers.get(key)
        answers.delete(key)
        const local = sentLocals[n]
        if (!local || local._rev !== sentRevs.get(key)) continue
        if (answer) {
          await applyServerRow(table, primaryKey, deletedField, answer, local)
        } else {
          await table.update(key, { _dirty: undefined } as UpdateSpec<R>)
        }
      }

      await applyServerRows(table, primaryKey, deletedField, [
        ...answers.values(),
      ])
    })
  }

  /* ---------- PULL ---------- */

  /** Pulls until a short page; returns whether the server held rows back. */
  private async pullRemote<R extends RowBase, CP>(
    cfg: Normalized<R, CP>
  ): Promise<boolean> {
    const { name, db, pull, deletedField } = cfg
    if (!pull) {
      console.info(`Pull handler not specified`)
      return false
    }

    const table = db.table(name) as Table<R, string>
    const primaryKey = primaryKeyOf(table)
    const meta = await this.meta<CP>(db, name)
    // The checkpoint lives in pullCheckpoint. lastPull is what app versions before
    // K35 stored and read (a date): left as they wrote it, so an old version
    // running on this device again (a tab left open, a rollback) still works
    let pullCheckpoint =
      meta.pullCheckpoint ?? pull.initialCheckpoint?.(meta.lastPull)
    let pending = false

    const batch =
      this.pageSizes.get(name) ?? pull.batchSize ?? Number.POSITIVE_INFINITY
    const metaTable = this.metaTable<CP>(db)
    const readStoredCheckpoint = async () =>
      (await metaTable.get(name))?.pullCheckpoint
    const sameCheckpoint = (a: CP | undefined, b: CP | undefined) =>
      a === undefined || b === undefined
        ? a === b
        : pull.compareCheckpoints(a, b) === 0
    const isBehind = (a: CP | undefined, b: CP | undefined) =>
      a !== undefined && b !== undefined && pull.compareCheckpoints(a, b) < 0
    // Moves the stored checkpoint to `reached`, unless that is behind what is
    // stored (a second tab on this database may be ahead) or the same
    const advanceCheckpoint = async (reached: CP, stored: CP | undefined) => {
      if (isBehind(reached, stored) || sameCheckpoint(reached, stored)) return
      await this.setMeta<CP>(db, name, { pullCheckpoint: reached })
    }

    // Writes a page with its checkpoint in one transaction: a pull cut short (a
    // reload, a killed app, an error) resumes after the last page stored, and a
    // checkpoint is never stored without its rows (K43). Another tab on this
    // database may have pulled past `storedAtRequest` while the page was on its
    // way, possibly newer copies of its rows: the page is dropped then, and the
    // pull goes on from where that tab got
    const applyPage = (
      records: R[],
      checkpoint: CP | undefined,
      storedAtRequest: CP | undefined
    ): Promise<{ stored: CP | undefined; dropped: boolean }> =>
      db.transaction('rw', table, metaTable, async (transaction) => {
        transaction.ctx = { fromServer: true } // hooks will detect this

        const stored = await readStoredCheckpoint()
        if (!sameCheckpoint(stored, storedAtRequest)) {
          return { stored, dropped: true }
        }
        await applyServerRows(table, primaryKey, deletedField, records)
        if (checkpoint !== undefined)
          await advanceCheckpoint(checkpoint, stored)
        return { stored: checkpoint ?? stored, dropped: false }
      })

    // The server's counter went backwards (its database restored into a new
    // cluster): nothing after the checkpoint this device sent would ever
    // arrive. Start again from the beginning, where every row comes once more
    const restartFromBeginning = async () => {
      console.warn(
        `[SyncManager] ${name}: the server is behind this device's checkpoint, pulling everything again`
      )
      const start = pull.initialCheckpoint?.(undefined)
      await this.setMeta<CP>(db, name, { pullCheckpoint: start })
      return start
    }

    let storedAtRequest = meta.pullCheckpoint
    let restarted = false
    while (true) {
      const sent = pullCheckpoint
      const page = await this.fetchPage(name, pull, sent, batch)
      pending = page.pending ?? false
      const { records, checkpoint } = page

      if (records.length === 0) {
        if (!restarted && isBehind(checkpoint, sent)) {
          restarted = true
          pullCheckpoint = storedAtRequest = await restartFromBeginning()
          continue
        }
        // Nothing new: the server's checkpoint (its watermark), or on a first
        // pull the one derived from an older version's lastPull
        const reached = checkpoint ?? pullCheckpoint
        if (reached !== undefined) {
          await db.transaction('rw', metaTable, async () =>
            advanceCheckpoint(reached, await readStoredCheckpoint())
          )
        }
        break
      }

      const { stored, dropped } = await applyPage(
        records,
        checkpoint,
        storedAtRequest
      )
      storedAtRequest = stored
      if (dropped) {
        pullCheckpoint = stored
        continue
      }
      // Without a checkpoint there is no next page to ask for; a short page is
      // the last
      if (checkpoint === undefined || records.length < batch) break
      pullCheckpoint = checkpoint
    }

    return pending
  }

  /**
   * One page from the pull handler. A page too big to arrive in time on this
   * link would time out every time: the next pulls ask for half the size,
   * down to MIN_PAGE_SIZE (K42).
   */
  private async fetchPage<R extends RowBase, CP>(
    name: string,
    pull: NonNullable<Normalized<R, CP>['pull']>,
    sent: CP | undefined,
    batch: number
  ): Promise<PullResponse<R, CP>> {
    try {
      return await pull.handler(sent as CP, batch)
    } catch (error) {
      if (syncFailureReason(error) === 'timeout' && batch > MIN_PAGE_SIZE) {
        this.pageSizes.set(name, Math.max(MIN_PAGE_SIZE, Math.floor(batch / 2)))
      }
      throw error
    }
  }

  /* ---------- meta helpers ---------- */

  private metaTable<CP>(db: Dexie) {
    return db.table('sync_meta') as Table<SyncMeta<CP>, string>
  }

  private async getPendingChangesCount<R extends RowBase, CP>(
    cfg: Normalized<R, CP>
  ): Promise<number> {
    if (!cfg.push) {
      return 0
    }

    const table = cfg.db.table(cfg.name) as Table<R, string>
    return table.where('_dirty').equals(1).count()
  }

  private async meta<CP>(db: Dexie, coll: string): Promise<SyncMeta<CP>> {
    const t = this.metaTable<CP>(db)
    let row = await t.get(coll)
    if (!row) {
      row = {
        collection: coll,
        lastPush: null,
        lastPushCheckpoint: undefined,
        pullCheckpoint: undefined,
      }
      await t.put(row)
    }
    return row
  }

  private setMeta<CP>(
    db: Dexie,
    coll: string,
    patch: Partial<SyncMeta<CP>>
  ): Promise<number> {
    return this.metaTable<CP>(db).update(
      coll,
      patch as unknown as UpdateSpec<SyncMeta<CP>>
    )
  }
}

const timeOf = (value: unknown): number =>
  new Date(value as string | number | Date).getTime()

/** The row field of a table's primary key (Dexie may prefix it, e.g. `$$id`). */
function primaryKeyOf<R extends RowBase>(table: Table<R, string>): string {
  const key = table.schema.primKey.name.replace(/^[^a-zA-Z]+/, '')
  if (!key) {
    throw new Error(
      `[SyncManager] Invalid primary key for table '${table.name}'`
    )
  }
  return key
}

/**
 * Whether an update changes a field of the row, its timestamp and the marker
 * aside. Dexie lists a field as modified when its value differs by reference,
 * so a row read and written back unchanged lists every date and file in it.
 */
function changesAnything(
  mods: object,
  stored: RowBase,
  updatedField: string
): boolean {
  return Object.entries(mods).some(
    ([field, value]) =>
      field !== updatedField &&
      field !== '_dirty' &&
      field !== '_rev' &&
      !sameValue(value, stored[field])
  )
}

/** By value; two files by what the database keeps of them besides the bytes. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a instanceof Blob && b instanceof Blob) {
    const [fa, fb] = [a as File, b as File]
    return (
      fa.size === fb.size &&
      fa.type === fb.type &&
      fa.name === fb.name &&
      fa.lastModified === fb.lastModified
    )
  }
  return isEqual(a, b)
}

/**
 * Whether a write comes from the server rather than from this device: its
 * transaction, or one it is nested in (a nested one gets its own `ctx`), is
 * marked `fromServer`, or it is a database upgrade (whose `mode` reads
 * readwrite; only the IndexedDB transaction says versionchange).
 */
function writtenAsServer(trans: Transaction | undefined): boolean {
  if (trans?.idbtrans?.mode === 'versionchange') return true
  for (let t = trans; t; t = t.parent) if (t.ctx?.fromServer) return true
  return false
}

/**
 * Writes a row as the server sent it, in a `fromServer` transaction, over
 * `local`, the phone's copy if it has one: removed if the server deleted it,
 * stored as sent if new, otherwise its fields written over the local ones, so
 * fields only the phone has (a photo's blob) stay. A copy written over a local
 * row is no longer unsent and gets a new local version, so a push answer still
 * on its way for the version it replaced isn't applied over it.
 */
async function applyServerRow<R extends RowBase>(
  table: Table<R, string>,
  primaryKey: string,
  deletedField: string,
  record: R,
  local: R | undefined
): Promise<void> {
  const key = record[primaryKey] as string
  if (record[deletedField]) {
    // Bypasses the soft delete addon: the server already deleted it
    if (local) await hardDelete(table, key)
    return
  }
  if (!local) {
    await table.put(record)
    return
  }

  const changeSet: Record<string, unknown> = { ...record }
  delete changeSet[primaryKey]
  if (local._rev !== undefined) {
    changeSet._dirty = undefined
    changeSet._rev = createId()
  }
  await table.update(key, changeSet as UpdateSpec<R>)
}

/**
 * Writes rows as the server sent them, inside the caller's `fromServer`
 * transaction. The server decides what the phone stores, and a row with unsent
 * changes is never overwritten: it waits for its push, whose answer is the
 * server's version (a refused edit comes back as the stored row). Applying a
 * newer server copy at once dropped an edit made while its push was in flight
 * before it was ever sent (K51), and would lose the events appended to a row.
 */
export async function applyServerRows<R extends RowBase>(
  table: Table<R, string>,
  primaryKey: string,
  deletedField: string,
  records: R[]
): Promise<void> {
  const locals = await table.bulkGet(
    records.map((record) => record[primaryKey] as string)
  )
  for (const [n, record] of records.entries()) {
    if (locals[n]?._dirty === 1) continue
    await applyServerRow(table, primaryKey, deletedField, record, locals[n])
  }
}

/* ---------- internal types ---------- */

type SyncMeta<CP> = {
  collection: string
  lastPush: Date | null
  lastPushCheckpoint: CP | undefined
  pullCheckpoint: CP | undefined
  // Written by app versions before K35 only, never by this one
  lastPull?: unknown
}

/** All optional fields have been filled with defaults */
type Normalized<R extends RowBase, CP> = Required<
  Pick<SyncableCfg<R, CP>, 'name' | 'db'>
> &
  Pick<SyncableCfg<R, CP>, 'push' | 'pull'> &
  Omit<SyncableCfg<R, CP>, 'name' | 'db' | 'push' | 'pull'> & {
    updatedField: string
    deletedField: string
  }
